import React from "react";
import { PRIX_PAQUET_DEFAUT, SEUIL_DEPISTAGE, bandFor, costPerYear, currentPerDay, isComplete, packYears, totalCigarettes, totalCostAtPrice, totalYears } from "./paquetAnneeRules";

const num = (s) => parseFloat(String(s).replace(",", "."));
const fr1 = (n) => (Math.round(n * 10) / 10).toLocaleString("fr-FR");
const frInt = (n) => Math.round(n).toLocaleString("fr-FR");
const euros = (n) => `${frInt(n)} €`;

const inputCls = "py-2 w-20 sm:w-24 text-center px-2 font-bold rounded-lg text-base sm:text-lg focus:outline-none focus:ring focus:ring-slate-300";

const BAND_CARD = {
    low: "from-emerald-900 to-emerald-700",
    mid: "from-amber-800 to-amber-600",
    high: "from-red-900 to-red-700",
};

function Row({ label, value, strong }) {
    return (
        <div className="flex items-center justify-between bg-stone-100 rounded-lg px-4 py-2">
            <span className="text-sm sm:text-base text-gray-900">{label}</span>
            <span className={`font-bold text-base sm:text-lg whitespace-nowrap ml-2 ${strong ? "text-emerald-800" : "text-gray-900"}`}>{value}</span>
        </div>
    );
}

function Field({ id, label, value, onChange, unit, decimal }) {
    return (
        <div>
            <label htmlFor={id} className="text-sm sm:text-base mb-2 block cursor-default">{label}</label>
            <div className="flex items-center gap-2">
                <input id={id} type="text" inputMode={decimal ? "decimal" : "numeric"} placeholder="—" className={inputCls}
                    value={value} onChange={(e) => onChange(e.target.value.replace(decimal ? /[^0-9.,]/g : /[^0-9]/g, ""))} />
                <span className="text-sm sm:text-base font-bold cursor-default">{unit}</span>
            </div>
        </div>
    );
}

const emptyPeriod = () => ({ perDay: "", years: "" });

export default function PaquetAnnee() {
    const [periods, setPeriods] = React.useState([emptyPeriod()]);
    const [price, setPrice] = React.useState(PRIX_PAQUET_DEFAUT);
    const parsed = periods.map((p) => ({ perDay: num(p.perDay), years: num(p.years) }));
    const ready = parsed.some(isComplete);
    const pa = packYears(parsed);
    const band = bandFor(pa);
    const packPrice = num(price);
    const priceOk = packPrice > 0 && packPrice <= 100;
    const perDayNow = currentPerDay(parsed);
    const perYear = priceOk ? costPerYear(perDayNow, packPrice) : 0;

    const update = (i, key, v) => setPeriods((prev) => prev.map((p, j) => (j === i ? { ...p, [key]: v } : p)));
    const remove = (i) => setPeriods((prev) => prev.filter((_, j) => j !== i));

    return (
        <>
            {periods.map((p, i) => (
                <div key={i} className="p-4 my-2 sm:my-3 bg-stone-100 rounded-lg">
                    {periods.length > 1 && (
                        <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-sm text-gray-900">Période {i + 1}</span>
                            <button type="button" onClick={() => remove(i)} className="text-xs underline text-gray-600 hover:text-gray-900">Supprimer</button>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-6">
                        <Field id={`pa-cig-${i}`} label="Cigarettes par jour" value={p.perDay} onChange={(v) => update(i, "perDay", v)} unit="/ jour" />
                        <Field id={`pa-years-${i}`} label="Pendant" value={p.years} onChange={(v) => update(i, "years", v)} unit="ans" decimal />
                    </div>
                    {p.perDay && num(p.perDay) > 200 && <p className="text-sm text-red-700 mt-2">Nombre de cigarettes par jour inhabituel : vérifiez la saisie.</p>}
                    {p.years && num(p.years) > 90 && <p className="text-sm text-red-700 mt-2">Durée inhabituelle : vérifiez la saisie.</p>}
                </div>
            ))}

            <div className="text-center my-2">
                <button type="button" onClick={() => setPeriods((prev) => [...prev, emptyPeriod()])}
                    className="text-sm font-semibold text-gray-900 bg-white border border-stone-300 hover:bg-stone-50 rounded-lg px-3 py-2">
                    + Ajouter une période
                </button>
                <p className="text-xs text-gray-600 mt-1">Votre consommation a changé au fil du temps ? Ajoutez une période par rythme.</p>
            </div>

            {!ready && (
                <div className="p-6 my-2 sm:my-4 bg-stone-50 border border-dashed border-stone-300 rounded-lg text-center">
                    <p className="text-gray-900 font-semibold text-sm sm:text-base">Indiquez combien de cigarettes vous fumez par jour, et depuis combien d'années</p>
                    <p className="text-gray-600 text-sm mt-2">Une cigarette roulée compte comme une cigarette. Un paquet contient 20 cigarettes.</p>
                </div>
            )}

            {ready && (
                <>
                    <div className={`p-4 my-2 sm:my-4 rounded-lg w-full bg-gradient-to-b ${BAND_CARD[band.key]} text-white text-center`}>
                        <div className="text-sm opacity-80">Consommation cumulée</div>
                        <div className="text-4xl sm:text-5xl font-bold my-1">{fr1(pa)} <span className="text-xl font-semibold">PA</span></div>
                        <div className="text-sm font-semibold">{band.label}</div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between bg-stone-100 rounded-lg px-4 py-2">
                            <span className="text-sm sm:text-base text-gray-900">Cigarettes fumées au total</span>
                            <span className="font-bold text-base sm:text-lg text-gray-900">≈ {frInt(totalCigarettes(parsed))}</span>
                        </div>
                        <div className="flex items-center justify-between bg-stone-100 rounded-lg px-4 py-2">
                            <span className="text-sm sm:text-base text-gray-900">Années de tabagisme</span>
                            <span className="font-bold text-base sm:text-lg text-gray-900">{fr1(totalYears(parsed))} ans</span>
                        </div>
                    </div>

                    <div className="p-4 mt-4 mb-2 bg-stone-100 rounded-lg">
                        <p className="font-bold text-gray-900 text-sm sm:text-base mb-2">Combien vous coûte le tabac ?</p>
                        <Field id="pa-price" label="Prix de votre paquet de 20" value={price} onChange={setPrice} unit="€" decimal />
                        <p className="text-xs text-gray-600 mt-1">Prix moyen en France en 2026 : environ 12,50 €. Tabac à rouler : indiquez le prix de l'équivalent de 20 cigarettes.</p>
                        {price && !priceOk && <p className="text-sm text-red-700 mt-2">Prix inhabituel : vérifiez la saisie.</p>}
                    </div>

                    {priceOk && (
                        <>
                            <p className="text-sm text-gray-700 mb-2">
                                En arrêtant aujourd'hui, au rythme de {frInt(perDayNow)} cigarette{perDayNow > 1 ? "s" : ""} par jour, vous économiseriez :
                            </p>
                            <div className="flex flex-col gap-2">
                                <Row label="Par mois" value={euros(perYear / 12)} strong />
                                <Row label="Par an" value={euros(perYear)} strong />
                                <Row label="En 10 ans" value={euros(perYear * 10)} strong />
                                <Row label="Dépensé au total (prix actuel)" value={`≈ ${euros(totalCostAtPrice(parsed, packPrice))}`} />
                            </div>
                            <p className="text-xs text-gray-600 mt-2">La dépense passée est estimée au prix d'aujourd'hui : le paquet coûtait beaucoup moins cher il y a quelques années, la dépense réelle a donc été plus faible.</p>
                        </>
                    )}

                    <p className="text-sm text-gray-700 mt-3">
                        {pa >= SEUIL_DEPISTAGE
                            ? "Vous atteignez le seuil de 20 paquets-années retenu pour le dépistage du cancer du poumon par scanner faible dose (programme Impulsion : 50 à 74 ans, fumeur ou sevré depuis moins de 15 ans). Parlez-en à votre médecin."
                            : "Il n'existe pas de seuil sans risque : chaque année de tabagisme en moins compte, et la durée pèse davantage que la quantité fumée chaque jour."}
                    </p>
                    <p className="text-xs text-gray-600 mt-2">Pour être aidé à arrêter : Tabac Info Service, 39 89 (accompagnement gratuit).</p>

                    <div className="text-center mt-3">
                        <button type="button" onClick={() => { setPeriods([emptyPeriod()]); setPrice(PRIX_PAQUET_DEFAUT); }} className="text-sm underline text-gray-700 hover:text-gray-900">Réinitialiser</button>
                    </div>
                </>
            )}
        </>
    );
}
